import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import useCountUp from './useCountUp';
import styles from './StatCard.module.scss';

/* ────────────────────────────────────────────────────────────
 * StatCard — KPI tile.
 *
 *   <StatCard
 *     label="Total orders"
 *     value={1284}
 *     delta={12.4}             // optional, % change
 *     trend="up"               // 'up' | 'down' | 'flat'
 *     icon={<Package />}       // optional leading icon
 *     accent="primary"         // 'primary' | 'success' | 'warning' | 'danger' | 'info'
 *     hint="vs. last week"
 *   />
 *
 * Numeral animates from 0 → value on first mount (MOTION.md §4.9).
 * Trend chip fades in 200ms after the numeral completes.
 * ──────────────────────────────────────────────────────────── */

const ACCENT_MAP = {
  primary: styles.accentPrimary,
  success: styles.accentSuccess,
  warning: styles.accentWarning,
  danger: styles.accentDanger,
  info: styles.accentInfo,
};

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };
const TREND_CLASS = {
  up: styles.trendUp,
  down: styles.trendDown,
  flat: styles.trendFlat,
};

const formatValue = (value, decimals, prefix, suffix) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return value ?? '';
  const fixed = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
  const withSeparators = fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${prefix || ''}${withSeparators}${suffix || ''}`;
};

const StatCard = ({
  label,
  value,
  delta,
  trend,
  hint,
  icon,
  accent,
  size,
  prefix,
  suffix,
  decimals,
  loading,
  className,
  children
}) => {
  const animated = useCountUp(typeof value === 'number' ? value : 0, {
    duration: typeof value === 'number' ? 600 : 0,
    decimals,
  });

  const display = useMemo(
    () =>
      typeof value === 'number'
        ? formatValue(animated, decimals, prefix, suffix)
        : value,
    [animated, value, decimals, prefix, suffix]
  );

  const TrendIcon = TREND_ICON[trend] || null;
  const classNames = [
    styles.card,
    ACCENT_MAP[accent],
    size === 'sm' ? styles.sizeSm : '',
    size === 'lg' ? styles.sizeLg : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        {icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
      </div>
      <div className={styles.value} aria-live="polite">
        {loading ? <span className={styles.valueSkeleton} /> : display}
      </div>
      {(delta !== null && delta !== undefined) || hint ? (
        <div className={styles.foot}>
          {delta !== null && delta !== undefined && (
            <span className={[styles.trend, TREND_CLASS[trend]].filter(Boolean).join(' ')}>
              {TrendIcon && <TrendIcon size={14} strokeWidth={2} />}
              {typeof delta === 'number' ? `${delta > 0 ? '+' : ''}${delta}%` : delta}
            </span>
          )}
          {hint && <span className={styles.hint}>{hint}</span>}
        </div>
      ) : null}
      {
        children
      }
    </div>
  );
};

StatCard.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  delta: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  trend: PropTypes.oneOf(['up', 'down', 'flat']),
  hint: PropTypes.node,
  icon: PropTypes.node,
  accent: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  decimals: PropTypes.number,
  loading: PropTypes.bool,
  className: PropTypes.string,
  children:PropTypes.node
};

StatCard.defaultProps = {
  value: 0,
  delta: null,
  trend: 'flat',
  hint: '',
  icon: null,
  accent: 'primary',
  size: 'md',
  prefix: '',
  suffix: '',
  decimals: 0,
  loading: false,
  className: '',
  children:""
};

export default StatCard;
